import React, { useEffect, useState } from "react";
import axios from "axios";
import { 
  FiMenu, FiX, FiLogOut, FiPlus, FiUsers, FiFolder, 
  FiCheckCircle, FiClock, FiAlertCircle, FiUser,
  FiMail, FiLock, FiGrid, FiBriefcase, FiSettings,
  FiArrowRight, FiArrowLeft, FiRotateCcw, FiPhone,
  FiCalendar, FiEdit2, FiTrash2, FiMessageCircle,
  FiDollarSign, FiTrendingUp, FiUpload, FiDownload,
  FiFileText, FiPieChart, FiHome, FiUserPlus
} from "react-icons/fi";

const base_url = "https://digitalmarketingwala.co.in/franchise-api";
// const base_url = "http://localhost/franchise-api";
axios.defaults.withCredentials = true;

function App() {
  const [authType, setAuthType] = useState(null);
  const [activePage, setActivePage] = useState("dashboard");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Admin states
  const [franchises, setFranchises] = useState([]);
  const [reports, setReports] = useState([]);
  const [franchiseName, setFranchiseName] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [reportTitle, setReportTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFranchise, setSelectedFranchise] = useState("");
  
  // Franchise states
  const [franchiseData, setFranchiseData] = useState(null);
  const [currentMonthReport, setCurrentMonthReport] = useState(null);
  const [previousMonthReport, setPreviousMonthReport] = useState(null);
  const [franchiseReports, setFranchiseReports] = useState([]);

  // Form states
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [franchiseCode, setFranchiseCode] = useState("");
  const [loginMode, setLoginMode] = useState("admin");

  useEffect(() => { 
    checkSession(); 
  }, []);

  const checkSession = async () => {
    try {
      const adminRes = await axios.get(`${base_url}/auth/check_auth.php`);
      if (adminRes.data.auth) {
        setAuthType("admin");
        loadAllAdminData();
        return;
      }

      const franchiseRes = await axios.get(`${base_url}/auth/franchise_check.php`);
      if (franchiseRes.data.auth) {
        setAuthType("franchise");
        setFranchiseData(franchiseRes.data);
        loadFranchiseReports(franchiseRes.data.id);
        return;
      }

      setAuthType(false);
    } catch {
      setAuthType(false);
    }
  };

  const adminLogin = async () => {
    try {
      const res = await axios.post(
        `${base_url}/auth/login.php`,
        { email, password }
      );
      if (res.data.status) {
        setAuthType("admin");
        loadAllAdminData();
      } else alert("Invalid Admin Login");
    } catch (error) {
      alert("Login failed");
    }
  };

  const franchiseLogin = async () => {
    try {
      const res = await axios.post(
        `${base_url}/auth/franchise_login.php`,
        { code: franchiseCode }
      );
      if (res.data.status) {
        const franchiseRes = await axios.get(`${base_url}/auth/franchise_check.php`);
        setAuthType("franchise");
        setFranchiseData(franchiseRes.data);
        loadFranchiseReports(franchiseRes.data.id);
      } else alert("Invalid Franchise Code");
    } catch (error) {
      alert("Login failed");
    }
  };

  const logout = async () => {
    await axios.get(`${base_url}/auth/logout.php`);
    await axios.get(`${base_url}/auth/franchise_logout.php`);
    setAuthType(false);
  };

  const loadAllAdminData = () => {
    fetchFranchises();
    fetchAllReports();
  };

  const fetchFranchises = async () => {
    try {
      const res = await axios.get(`${base_url}/get_franchises.php`);
      setFranchises(res.data);
    } catch (error) {
      console.error("Error fetching franchises:", error);
    }
  };

  const fetchAllReports = async () => {
    try {
      const res = await axios.get(`${base_url}/get_all_reports.php`);
      setReports(res.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
    }
  };

  const loadFranchiseReports = async (franchiseId) => {
    try {
      const res = await axios.get(`${base_url}/get_franchise_reports.php?franchise_id=${franchiseId}`);
      setFranchiseReports(res.data);
      
      // Set current and previous month reports
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();
      
      const current = res.data.find(r => 
        r.month === currentMonth && r.year === currentYear
      );
      
      const prevDate = new Date(now.setMonth(now.getMonth() - 1));
      const prevMonth = prevDate.getMonth() + 1;
      const prevYear = prevDate.getFullYear();
      
      const previous = res.data.find(r => 
        r.month === prevMonth && r.year === prevYear
      );
      
      setCurrentMonthReport(current);
      setPreviousMonthReport(previous);
    } catch (error) {
      console.error("Error loading franchise reports:", error);
    }
  };

  const addFranchise = async () => {
    if (!franchiseName) {
      alert("Please enter franchise name");
      return;
    }

    try {
      const res = await axios.post(`${base_url}/add_franchise.php`, {
        name: franchiseName
      });

      if (res.data.status) {
        alert(`Franchise added successfully! Login Code: ${res.data.code}`);
        setFranchiseName("");
        fetchFranchises();
      }
    } catch (error) {
      alert("Failed to add franchise");
    }
  };

  const addReport = async () => {
    if (!selectedFranchise || !selectedMonth || !selectedYear || !reportTitle) {
      alert("Please fill all fields");
      return;
    }

    const formData = new FormData();
    formData.append("franchise_id", selectedFranchise);
    formData.append("month", selectedMonth);
    formData.append("year", selectedYear);
    formData.append("title", reportTitle);
    if (selectedFile) {
      formData.append("document", selectedFile);
    }

    try {
      await axios.post(`${base_url}/add_report.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setSelectedFranchise("");
      setSelectedMonth("");
      setSelectedYear(new Date().getFullYear());
      setReportTitle("");
      setSelectedFile(null);
      
      fetchAllReports();
      alert("Report added successfully!");
    } catch (error) {
      alert("Failed to add report");
    }
  };

  const downloadDocument = async (reportId, filename) => {
    try {
      const response = await axios.get(`${base_url}/download_document.php?id=${reportId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert("Failed to download document");
    }
  };

  const formatMonth = (month) => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return months[month - 1];
  };

  if (authType === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking session...</p>
        </div>
      </div>
    );
  }

  if (!authType) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="bg-white/80 backdrop-blur-lg p-8 rounded-2xl shadow-xl w-full max-w-md border border-white/20">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Franchise Management System
            </h1>
            <p className="text-gray-500 mt-2">Manage your franchise reports efficiently</p>
          </div>

          <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setLoginMode("admin")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                loginMode === "admin" 
                  ? "bg-white text-blue-600 shadow-sm" 
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              Admin
            </button>
            <button
              onClick={() => setLoginMode("franchise")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                loginMode === "franchise" 
                  ? "bg-white text-blue-600 shadow-sm" 
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              Franchise
            </button>
          </div>

          {loginMode === "admin" ? (
            <div className="space-y-4">
              <div className="relative">
                <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Email"
                  type="email"
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
              <div className="relative">
                <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Password"
                  onChange={e => setPassword(e.target.value)}
                />
              </div>
              <button
                onClick={adminLogin}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all transform hover:scale-[1.02]"
              >
                Sign In
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative">
                <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Franchise Code"
                  onChange={e => setFranchiseCode(e.target.value)}
                />
              </div>
              <button
                onClick={franchiseLogin}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition-all transform hover:scale-[1.02]"
              >
                Access Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (authType === "franchise") {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Franchise Navigation */}
        <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg flex items-center justify-center">
                  <FiHome className="text-white" />
                </div>
                <h1 className="text-xl font-semibold text-gray-800">Franchise Portal</h1>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <span className="text-sm text-gray-600 block">Welcome,</span>
                  <span className="text-sm font-semibold text-gray-800">{franchiseData?.name}</span>
                </div>
                <button
                  onClick={logout}
                  className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <FiLogOut />
                  <span className="hidden sm:inline">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Franchise Dashboard */}
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Dashboard</h2>
            <p className="text-gray-600">View your monthly reports and documents</p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Reports</p>
                  <p className="text-2xl font-bold text-gray-800">{franchiseReports.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                  <FiFileText size={24} />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Current Month</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {currentMonthReport ? formatMonth(currentMonthReport.month) : 'No report'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
                  <FiPieChart size={24} />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Previous Month</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {previousMonthReport ? formatMonth(previousMonthReport.month) : 'No report'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600">
                  <FiCalendar size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* Reports Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Current Month Report */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b bg-blue-50 border-blue-200">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <FiPieChart className="text-blue-600" />
                  This Month's Report
                </h3>
              </div>
              <div className="p-6">
                {currentMonthReport ? (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Month</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {formatMonth(currentMonthReport.month)} {currentMonthReport.year}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Title</p>
                      <p className="text-gray-800">{currentMonthReport.title}</p>
                    </div>
                    {currentMonthReport.filename && (
                      <button
                        onClick={() => downloadDocument(currentMonthReport.id, currentMonthReport.filename)}
                        className="flex items-center gap-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <FiDownload />
                        Download Document
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No report available for this month</p>
                  </div>
                )}
              </div>
            </div>

            {/* Previous Month Report */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b bg-purple-50 border-purple-200">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <FiCalendar className="text-purple-600" />
                  Previous Month's Report
                </h3>
              </div>
              <div className="p-6">
                {previousMonthReport ? (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Month</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {formatMonth(previousMonthReport.month)} {previousMonthReport.year}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Title</p>
                      <p className="text-gray-800">{previousMonthReport.title}</p>
                    </div>
                    {previousMonthReport.filename && (
                      <button
                        onClick={() => downloadDocument(previousMonthReport.id, previousMonthReport.filename)}
                        className="flex items-center gap-2 bg-purple-50 text-purple-600 px-4 py-2 rounded-lg hover:bg-purple-100 transition-colors"
                      >
                        <FiDownload />
                        Download Document
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No report available for previous month</p>
                  </div>
                )}
              </div>
            </div>

            {/* All Reports */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b bg-gray-50 border-gray-200">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <FiFileText />
                  All Reports History
                </h3>
              </div>
              <div className="divide-y divide-gray-200">
                {franchiseReports.map(report => (
                  <div key={report.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <p className="font-medium text-gray-800">{report.title}</p>
                        <p className="text-sm text-gray-600">
                          {formatMonth(report.month)} {report.year}
                        </p>
                      </div>
                      {report.filename && (
                        <button
                          onClick={() => downloadDocument(report.id, report.filename)}
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                        >
                          <FiDownload />
                          Download
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {franchiseReports.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    No reports available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin Panel
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <FiGrid /> },
    { id: 'franchises', label: 'Franchises', icon: <FiUsers /> },
    { id: 'reports', label: 'Reports', icon: <FiFileText /> },
    { id: 'add-report', label: 'Add Report', icon: <FiUpload /> },
    { id: 'settings', label: 'Settings', icon: <FiSettings /> },
  ];

  const stats = [
    { label: 'Total Franchises', value: franchises.length, color: 'blue', icon: <FiUsers /> },
    { label: 'Total Reports', value: reports.length, color: 'green', icon: <FiFileText /> },
    { label: 'Reports This Month', value: reports.filter(r => {
      const now = new Date();
      return r.month === now.getMonth() + 1 && r.year === now.getFullYear();
    }).length, color: 'purple', icon: <FiPieChart /> },
    { label: 'Active Franchises', value: franchises.length, color: 'orange', icon: <FiBriefcase /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-20">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <FiGrid className="text-white" />
            </div>
            <h1 className="text-xl font-semibold text-gray-800">Admin Panel</h1>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-10 bg-black bg-opacity-50" onClick={() => setMobileMenuOpen(false)}>
          <div className="fixed left-0 top-16 bottom-0 w-64 bg-white shadow-xl" onClick={e => e.stopPropagation()}>
            <nav className="p-4">
              {menuItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActivePage(item.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                    activePage === item.id
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              ))}
              <button
                onClick={logout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 mt-4"
              >
                <FiLogOut />
                <span>Logout</span>
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 min-h-0 bg-white border-r border-gray-200">
          <div className="flex items-center h-16 px-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <FiGrid className="text-white" />
              </div>
              <h1 className="text-xl font-semibold text-gray-800">Admin Panel</h1>
            </div>
          </div>
          <nav className="flex-1 px-2 py-4 space-y-1">
            {menuItems.map(item => (
              <button
                key={item.id}
                onClick={() => setActivePage(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activePage === item.id
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={logout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
            >
              <FiLogOut />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:pl-64">
        <main className="pt-16 lg:pt-0 min-h-screen">
          <div className="p-4 sm:p-6 lg:p-8">
            {/* Dashboard */}
            {activePage === 'dashboard' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
                  {stats.map((stat, index) => (
                    <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                          <p className="text-3xl font-bold text-gray-800">{stat.value}</p>
                        </div>
                        <div className={`w-12 h-12 bg-${stat.color}-100 rounded-lg flex items-center justify-center text-${stat.color}-600`}>
                          {stat.icon}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Recent Reports */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b bg-gray-50 border-gray-200">
                    <h3 className="font-semibold text-gray-800">Recent Reports</h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {reports.slice(0, 5).map(report => {
                      const franchise = franchises.find(f => f.id === report.franchise_id);
                      return (
                        <div key={report.id} className="p-4 hover:bg-gray-50 transition-colors">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div>
                              <p className="font-medium text-gray-800">{report.title}</p>
                              <p className="text-sm text-gray-600">
                                {franchise?.name} - {formatMonth(report.month)} {report.year}
                              </p>
                            </div>
                            {report.filename && (
                              <button
                                onClick={() => downloadDocument(report.id, report.filename)}
                                className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                              >
                                <FiDownload />
                                Download
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {reports.length === 0 && (
                      <div className="p-8 text-center text-gray-500">
                        No reports added yet
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Franchises Management */}
            {activePage === 'franchises' && (
              <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-6">Manage Franchises</h2>
                  
                  <div className="flex flex-col sm:flex-row gap-4 mb-8">
                    <input
                      value={franchiseName}
                      onChange={e => setFranchiseName(e.target.value)}
                      placeholder="Enter franchise name"
                      className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={addFranchise}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-2 rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition-all flex items-center justify-center gap-2"
                    >
                      <FiUserPlus />
                      Add Franchise
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {franchises.map(franchise => (
                      <div key={franchise.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 font-semibold">
                            {franchise.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{franchise.name}</p>
                            <p className="text-xs text-gray-500">Login Code: {franchise.code}</p>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">
                          Reports: {reports.filter(r => r.franchise_id === franchise.id).length}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Reports List */}
            {activePage === 'reports' && (
              <div className="max-w-6xl mx-auto">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b bg-gray-50 border-gray-200">
                    <h3 className="font-semibold text-gray-800">All Reports</h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {reports.map(report => {
                      const franchise = franchises.find(f => f.id === report.franchise_id);
                      return (
                        <div key={report.id} className="p-4 hover:bg-gray-50 transition-colors">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div>
                              <p className="font-medium text-gray-800">{report.title}</p>
                              <p className="text-sm text-gray-600">
                                Franchise: {franchise?.name || 'Unknown'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatMonth(report.month)} {report.year}
                              </p>
                            </div>
                            {report.filename && (
                              <button
                                onClick={() => downloadDocument(report.id, report.filename)}
                                className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                              >
                                <FiDownload />
                                Download
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {reports.length === 0 && (
                      <div className="p-8 text-center text-gray-500">
                        No reports added yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Add Report */}
            {activePage === 'add-report' && (
              <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-6">Add Monthly Report</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Franchise *
                      </label>
                      <select
                        value={selectedFranchise}
                        onChange={e => setSelectedFranchise(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Choose Franchise</option>
                        {franchises.map(franchise => (
                          <option key={franchise.id} value={franchise.id}>
                            {franchise.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Report Title *
                      </label>
                      <input
                        type="text"
                        value={reportTitle}
                        onChange={e => setReportTitle(e.target.value)}
                        placeholder="e.g., Monthly Sales Report"
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Month *
                        </label>
                        <select
                          value={selectedMonth}
                          onChange={e => setSelectedMonth(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select Month</option>
                          {[1,2,3,4,5,6,7,8,9,10,11,12].map(month => (
                            <option key={month} value={month}>
                              {formatMonth(month)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Year *
                        </label>
                        <input
                          type="number"
                          value={selectedYear}
                          onChange={e => setSelectedYear(e.target.value)}
                          min="2020"
                          max="2030"
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Upload Document (PDF/Excel)
                      </label>
                      <input
                        type="file"
                        onChange={e => setSelectedFile(e.target.files[0])}
                        accept=".pdf,.xls,.xlsx,.csv"
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Supported formats: PDF, Excel (.xls, .xlsx), CSV
                      </p>
                    </div>

                    <button
                      onClick={addReport}
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center justify-center gap-2"
                    >
                      <FiUpload />
                      Add Report
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Settings */}
            {activePage === 'settings' && (
              <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-6">Settings</h2>
                  <p className="text-gray-500">Settings panel coming soon...</p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;